import { useQuery, useQueryClient } from '@tanstack/react-query'
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { ApiError } from '../api/client'
import {
  addSurveySpot,
  deleteSurveySpot,
  dismissSuggestion,
  finishSurvey,
  locateDevices,
  saveLayout,
  startSurvey,
  suggestionsQuery,
  surveyQuery,
  type FloorItem,
  type FloorPage,
  type Layout,
  type SuggestionsPage,
  type SurveyPage,
  type Wall,
} from '../api/floorPlans'
import { toast } from '../ui/notify'
import { createAutosave, type Autosave } from './autosave'
import type { CoverageRequest } from './coverage'
import { cmToPx, distanceM, pxToCm, rescaleCm, scaleFromCalib, type Pt } from './geometry'
import { commit, redo, start, undo, type History } from './history'
import { SNAP_M, scaleBar } from './grid'
import type { MapSelection, MapSuggestion, MapTool } from './mapTypes'
import { MATERIALS, WALL_DB, type Material } from './predict'
import { acceptAll, acceptSuggestion, locateStatus, planSuggestions, SUGGEST_EXPLAIN, type PlanSuggestions, type Suggestion } from './suggest'
import { useCoverage } from './useCoverage'
import { apOf, COLORS, itemLabel, mapItems, mapSpots, mapWalls, predictInput, surveySamples } from './viewModel'
import './FloorPlan.css'

/** OpenLayers se incarca numai aici, cand se deschide un plan. */
const FloorPlanMap = lazy(() => import('./FloorPlanMap'))

type Layer = 'none' | 'survey' | 'predicted'

/**
 * Planul unui etaj (Connection Quality, faza 5): `mode: 'edit'` pentru cine
 * aseaza AP-uri, aparate si pereti; `mode: 'view'` pentru straturi (AP-urile
 * colorate dupa experienta clientilor, aparatele cu semnalul lor, harta de
 * acoperire din survey, predictia prin pereti) si pentru survey.
 *
 * Starea planului (calibrare, pereti, item-uri) sta in `History` (undo/redo)
 * si pleaca singura la server (autosave.ts: dupa o secunda de liniste, si
 * imediat la `pagehide`). Coordonatele se tin in cm; harta primeste pixeli.
 */
/**
 * `lead` si `trail` intra in ACELASI rand cu uneltele: pagina etajului pune
 * acolo "Upload/Replace plan" (stanga) si comutatorul View/Edit (dreapta).
 */
export function FloorPlan({ page, mode, lead, trail }: { page: FloorPage; mode: 'view' | 'edit'; lead?: ReactNode; trail?: ReactNode }) {
  const floor = page.floor
  const editable = mode === 'edit' && page.canWrite
  const query = useQueryClient()

  const initial = useMemo<Layout>(
    () => ({ calib: floor.calib, walls: floor.walls, items: page.items, heat: floor.heat }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [floor.id],
  )
  const [hist, setHist] = useState<History<Layout>>(() => start(initial))
  const layout = hist.present
  /* Etajul desenat fara plan are scara lui, fixa, fara calibrare. */
  const canvas = floor.canvas === true && floor.image_url === null && floor.scale_px_per_m !== null && floor.image_w_px !== null && floor.image_h_px !== null
  const scale = canvas ? floor.scale_px_per_m : scaleFromCalib(layout.calib)
  const [zoom, setZoom] = useState<number | null>(null)
  const thresholds = layout.heat

  const [tool, setTool] = useState<MapTool>('pan')
  const [armed, setArmed] = useState<string | null>(null)
  const [points, setPoints] = useState<Pt[]>([])
  const [meters, setMeters] = useState('')
  const [material, setMaterial] = useState<Material>('drywall')
  const [selection, setSelection] = useState<MapSelection | null>(null)
  const [layer, setLayer] = useState<Layer>('none')
  const [apChoice, setApChoice] = useState<string>('best')
  const [surveyIds, setSurveyIds] = useState<number[]>([])
  const [laptop, setLaptop] = useState<string>(page.surveyDevices.length === 1 ? String(page.surveyDevices[0].id) : '')
  const [saveState, setSaveState] = useState<'saved' | 'saving' | 'error'>('saved')
  const [busy, setBusy] = useState(false)

  /* ------------------------------------------------------------ salvarea */
  const autosave = useRef<Autosave<Layout> | null>(null)
  useEffect(() => {
    const a = createAutosave<Layout>(
      async (state, opts) => {
        setSaveState('saving')
        await saveLayout(floor.id, state, opts.keepalive)
        setSaveState('saved')
      },
      1000,
      (e) => {
        setSaveState('error')
        toast(e instanceof ApiError && e.message ? e.message : 'The plan could not be saved.', 'Floor plan', 'error')
      },
    )
    autosave.current = a
    return () => {
      a.dispose()
      void query.invalidateQueries({ queryKey: ['settings', 'floor-plans'] })
    }
  }, [floor.id, query])

  useEffect(() => {
    if (layout !== initial && editable) autosave.current?.schedule(layout)
  }, [layout, initial, editable])

  const change = useCallback((next: Layout) => setHist((h) => commit(h, next)), [])

  /* ------------------------------------------------------------ survey */
  const survey = useQuery({ ...surveyQuery(floor.id, surveyIds), enabled: scale !== null })
  const surveyPage: SurveyPage | undefined = survey.data
  const openSurvey = surveyPage?.open ?? null
  const setSurvey = (next: SurveyPage) => query.setQueryData(surveyQuery(floor.id, surveyIds).queryKey, next)

  useEffect(() => {
    if (openSurvey === null && tool === 'survey') setTool('pan')
  }, [openSurvey, tool])

  /* ------------------------------------------------------------ sugestiile */
  const sugg = useQuery({ ...suggestionsQuery(floor.id), enabled: editable && scale !== null })
  const setSugg = (next: SuggestionsPage) => query.setQueryData(suggestionsQuery(floor.id).queryKey, next)
  const suggestions = useMemo(() => planSuggestions(sugg.data, layout.items, page.aps), [sugg.data, layout.items, page.aps])
  /* Un punct nou masurat schimba sugestiile: se recitesc. */
  const measured = surveyPage?.spots.filter((s) => s.status === 'ok').length ?? 0
  useEffect(() => {
    if (measured > 0) void query.invalidateQueries({ queryKey: suggestionsQuery(floor.id).queryKey })
  }, [measured, floor.id, query])

  /* ------------------------------------------------------------ straturile */
  const widthM = scale ? (floor.image_w_px ?? 0) / scale : 0
  const heightM = scale ? (floor.image_h_px ?? 0) / scale : 0
  const coverageReq: CoverageRequest | null = useMemo(() => {
    if (!scale) return null
    if (layer === 'survey' && surveyPage) {
      const samples = surveySamples(surveyPage.spots, surveyPage.bssidAps, apChoice)
      if (samples.length === 0) return null
      return { kind: 'survey', widthM, heightM, cellM: thresholds.cell_m, maskM: thresholds.mask_m, thresholds, samples }
    }
    if (layer === 'predicted') {
      const input = predictInput(layout.items, layout.walls)
      if (input.aps.length === 0) return null
      return { kind: 'predict', widthM, heightM, cellM: thresholds.cell_m, thresholds, ...input }
    }
    return null
  }, [scale, layer, surveyPage, apChoice, widthM, heightM, thresholds, layout.items, layout.walls])
  const coverage = useCoverage(coverageReq, scale)

  const itemSel = selection?.kind === 'item' ? selection.index : null
  const wallSel = selection?.kind === 'wall' ? selection.index : null
  const spotSel = selection?.kind === 'spot' ? selection.id : null
  const items = useMemo(
    () => (scale ? mapItems(layout.items, scale, page.aps, page.devices, page.signals, thresholds, itemSel) : []),
    [layout.items, scale, page.aps, page.devices, page.signals, thresholds, itemSel],
  )
  const walls = useMemo(() => (scale ? mapWalls(layout.walls, scale, wallSel) : []), [layout.walls, scale, wallSel])
  const spots = useMemo(
    () => (scale && surveyPage ? mapSpots(surveyPage.spots, scale, surveyPage.bssidAps, apChoice, thresholds, spotSel) : []),
    [scale, surveyPage, apChoice, thresholds, spotSel],
  )
  const suggestionSel = selection?.kind === 'suggestion' ? selection.id : null
  const mapSuggestions = useMemo<MapSuggestion[]>(
    () =>
      scale && editable
        ? suggestions.pending.map((g) => ({
            id: g.id,
            kind: g.kind,
            x: cmToPx(g.x_cm, scale),
            y: cmToPx(g.y_cm, scale),
            radiusPx: cmToPx(g.radius_cm, scale),
            label: g.label,
            selected: suggestionSel === g.id,
          }))
        : [],
    [scale, editable, suggestions.pending, suggestionSel],
  )

  /* ------------------------------------------------------------ gesturile */
  const cm = (p: Pt) => ({ x: pxToCm(p.x, scale ?? 1), y: pxToCm(p.y, scale ?? 1) })

  const place = (payload: string, p: Pt) => {
    if (!scale) return
    const at = cm(p)
    const base: FloorItem = { kind: 'note', ap_group_key: null, device_id: null, label: null, x_cm: at.x, y_cm: at.y, attrs: null }
    let next: FloorItem | null = null
    if (payload.startsWith('ap:')) {
      const key = payload.slice(3)
      if (layout.items.some((i) => i.kind === 'ap' && i.ap_group_key === key)) {
        change({ ...layout, items: layout.items.map((i) => (i.kind === 'ap' && i.ap_group_key === key ? { ...i, x_cm: at.x, y_cm: at.y } : i)) })
        return
      }
      next = { ...base, kind: 'ap', ap_group_key: key }
    } else if (payload.startsWith('device:')) {
      const id = Number(payload.slice(7))
      if (layout.items.some((i) => i.kind === 'device' && i.device_id === id)) {
        change({ ...layout, items: layout.items.map((i) => (i.kind === 'device' && i.device_id === id ? { ...i, x_cm: at.x, y_cm: at.y } : i)) })
        return
      }
      next = { ...base, kind: 'device', device_id: id }
    } else if (payload === 'planned') {
      next = { ...base, kind: 'ap', label: 'Planned AP', attrs: { band: '5', tx_dbm: 20 } }
    } else if (payload === 'note') {
      next = { ...base, label: 'Note' }
    }
    if (next) {
      change({ ...layout, items: [...layout.items, next] })
      setSelection({ kind: 'item', index: layout.items.length })
    }
  }

  const onPoint = (p: Pt) => {
    if (tool === 'calibrate') {
      setPoints((prev) => (prev.length >= 2 ? [p] : [...prev, p]))
      return
    }
    if (tool === 'measure') {
      setPoints((prev) => (prev.length >= 2 ? [p] : [...prev, p]))
      return
    }
    if (tool === 'place' && armed) {
      place(armed, p)
      setArmed(null)
      setTool('pan')
      return
    }
    if (tool === 'survey' && openSurvey !== null && scale) {
      const at = cm(p)
      setBusy(true)
      addSurveySpot(floor.id, openSurvey, at.x, at.y)
        .then((next) => setSurvey(next))
        .catch((e: unknown) => toast(e instanceof ApiError && e.message ? e.message : 'The spot could not be added.', 'Survey', 'error'))
        .finally(() => setBusy(false))
    }
  }

  const applyCalibration = () => {
    const m = Number(meters.replace(',', '.'))
    if (points.length !== 2 || !(m > 0)) return
    const calib = { x1: round2(points[0].x), y1: round2(points[0].y), x2: round2(points[1].x), y2: round2(points[1].y), meters: m }
    const next = scaleFromCalib(calib)
    if (next === null) {
      toast('The two points are too close together.', 'Calibration', 'error')
      return
    }
    /* Ce e deja pe plan ramane pe acelasi pixel. */
    const k = (v: number) => (scale ? rescaleCm(v, scale, next) : v)
    change({
      ...layout,
      calib,
      walls: layout.walls.map((w) => ({ ...w, x1: k(w.x1), y1: k(w.y1), x2: k(w.x2), y2: k(w.y2) })),
      items: layout.items.map((i) => ({ ...i, x_cm: k(i.x_cm), y_cm: k(i.y_cm) })),
    })
    setPoints([])
    setMeters('')
    setTool('pan')
    /* Punctele de survey le muta serverul; se recitesc. */
    void query.invalidateQueries({ queryKey: ['settings', 'floor-plans', floor.id, 'survey'] })
  }

  const guide = useMemo(() => {
    if ((tool !== 'calibrate' && tool !== 'measure') || points.length === 0) return null
    const [a, b] = [points[0], points[1] ?? points[0]]
    const label = tool === 'measure' && points.length === 2 && scale ? `${distanceM(a, b, scale).toFixed(2)} m` : ''
    return { x1: a.x, y1: a.y, x2: b.x, y2: b.y, label }
  }, [tool, points, scale])

  const removeSelected = useCallback(() => {
    if (!editable || !selection) return
    if (selection.kind === 'item') change({ ...layout, items: layout.items.filter((_, i) => i !== selection.index) })
    if (selection.kind === 'wall') change({ ...layout, walls: layout.walls.filter((_, i) => i !== selection.index) })
    setSelection(null)
  }, [editable, selection, layout, change])

  /* Tastatura: Ctrl+Z, Ctrl+Y / Ctrl+Shift+Z, Delete. */
  useEffect(() => {
    if (!editable) return
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null
      if (t && (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA')) return
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault()
        setHist((h) => (e.shiftKey ? redo(h) : undo(h)))
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
        e.preventDefault()
        setHist(redo)
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        removeSelected()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [editable, removeSelected])

  const pick = (t: MapTool) => {
    setTool(t)
    setPoints([])
    if (t !== 'place') setArmed(null)
  }

  const arm = (payload: string) => {
    setArmed(payload)
    setTool('place')
    setPoints([])
  }

  /* ------------------------------------------------------------ survey: butoane */
  const onStartSurvey = () => {
    if (!laptop) return
    setBusy(true)
    startSurvey(floor.id, Number(laptop))
      .then((next) => {
        setSurveyIds(next.selected)
        query.setQueryData(surveyQuery(floor.id, next.selected).queryKey, next)
        setTool('survey')
        setLayer('survey')
      })
      .catch((e: unknown) => toast(e instanceof ApiError && e.message ? e.message : 'The survey could not be started.', 'Survey', 'error'))
      .finally(() => setBusy(false))
  }

  const onFinishSurvey = () => {
    if (openSurvey === null) return
    setBusy(true)
    finishSurvey(floor.id, openSurvey)
      .then((next) => setSurvey(next))
      .catch((e: unknown) => toast(e instanceof ApiError && e.message ? e.message : 'The survey could not be finished.', 'Survey', 'error'))
      .finally(() => setBusy(false))
  }

  const onDeleteSpot = (spotId: number, surveyId: number) => {
    setBusy(true)
    deleteSurveySpot(floor.id, surveyId, spotId)
      .then(() => {
        setSelection(null)
        return query.invalidateQueries({ queryKey: ['settings', 'floor-plans', floor.id, 'survey'] })
      })
      .catch((e: unknown) => toast(e instanceof ApiError && e.message ? e.message : 'The spot could not be deleted.', 'Survey', 'error'))
      .finally(() => setBusy(false))
  }

  /* ------------------------------------------------------------ sugestii: butoane */
  const onAccept = (g: Suggestion) => {
    change({ ...layout, items: acceptSuggestion(layout.items, g) })
    setSelection(null)
  }

  const onAcceptAll = () => {
    change({ ...layout, items: acceptAll(layout.items, suggestions.pending) })
    setSelection(null)
  }

  const onDismiss = (g: Suggestion) => {
    const body = g.kind === 'ap' ? { kind: 'ap' as const, key: g.key as string } : { kind: 'device' as const, device_id: g.device_id as number, spot_id: g.spot_id as number }
    setBusy(true)
    dismissSuggestion(floor.id, body)
      .then((next) => {
        setSugg(next)
        setSelection(null)
      })
      .catch((e: unknown) => toast(e instanceof ApiError && e.message ? e.message : 'The suggestion could not be dismissed.', 'Suggestions', 'error'))
      .finally(() => setBusy(false))
  }

  const onLocate = (ids: number[]) => {
    setBusy(true)
    locateDevices(floor.id, ids)
      .then((next) => {
        setSugg(next)
        toast(`Measuring ${next.located ?? 0} device(s). Their suggested position appears in a minute.`, 'Locate devices', 'success')
      })
      .catch((e: unknown) => toast(e instanceof ApiError && e.message ? e.message : 'The devices could not be located.', 'Locate devices', 'error'))
      .finally(() => setBusy(false))
  }

  /* ------------------------------------------------------------ desenul */
  const hasImage = floor.image_url !== null && floor.image_w_px !== null && floor.image_h_px !== null
  const hasSurface = hasImage || canvas
  const bar = hasSurface && scale && zoom ? scaleBar(scale, zoom) : null
  const hint = !hasSurface
    ? page.canWrite
      ? 'Upload the plan, or draw the floor without one.'
      : 'This floor has no plan yet.'
    : !scale
      ? editable
        ? 'Calibrate the plan: choose Calibrate, tap two points whose distance you know, and enter it in meters.'
        : 'This plan is not calibrated yet.'
      : tool === 'calibrate'
        ? points.length < 2
          ? `Tap point ${points.length + 1} of 2.`
          : 'Enter the real distance between the two points.'
        : tool === 'measure'
          ? points.length === 2 && scale
            ? `Distance: ${distanceM(points[0], points[1], scale).toFixed(2)} m. Tap again to measure another.`
            : 'Tap two points to measure the distance.'
          : tool === 'wall'
            ? `Tap the start and the end of the wall (${material}, ${WALL_DB[material]} dB).`
            : tool === 'place' && armed
              ? 'Tap the plan where it goes.'
              : tool === 'survey'
                ? 'Tap the plan where you are standing with the laptop. Keep still until the spot turns colored.'
                : editable
                  ? canvas
                    ? 'Draw the walls with Wall (they snap to the 0.5 m grid), then drag items from the lists onto the floor, or tap one and then tap the floor.'
                    : 'Drag items from the lists onto the plan, or tap one and then tap the plan.'
                  : ''

  return (
    <div className={`fp-shell fp-mode-${mode}`} data-floor={floor.id}>
      <div className="fp-toolbar" role="toolbar" aria-label="Floor plan tools">
        {lead}
        <ToolButton tool="pan" current={tool} onPick={pick} icon="fa-hand-paper" label={editable ? 'Select / move' : 'Pan'} />
        {editable && !canvas ? <ToolButton tool="calibrate" current={tool} onPick={pick} icon="fa-ruler-combined" label="Calibrate" disabled={!hasImage} /> : null}
        <ToolButton tool="measure" current={tool} onPick={pick} icon="fa-ruler" label="Measure" disabled={!scale} />
        {editable ? (
          <>
            <ToolButton tool="wall" current={tool} onPick={pick} icon="fa-grip-lines" label="Wall" disabled={!scale} />
            <select className="form-control input-sm fp-material" aria-label="Wall material" value={material} onChange={(e) => setMaterial(e.target.value as Material)}>
              {MATERIALS.map((m) => (
                <option key={m} value={m}>
                  {m} ({WALL_DB[m]} dB)
                </option>
              ))}
            </select>
            <button type="button" className="btn btn-default fp-btn" onClick={() => setHist(undo)} disabled={hist.past.length === 0} aria-label="Undo">
              <i className="fas fa-undo" aria-hidden="true"></i>
            </button>
            <button type="button" className="btn btn-default fp-btn" onClick={() => setHist(redo)} disabled={hist.future.length === 0} aria-label="Redo">
              <i className="fas fa-redo" aria-hidden="true"></i>
            </button>
            <span className={`fp-save is-${saveState}`} data-save={saveState}>
              {saveState === 'saving' ? 'Saving...' : saveState === 'error' ? 'Not saved' : 'Saved'}
            </span>
          </>
        ) : null}
        {openSurvey !== null && page.canWrite ? <ToolButton tool="survey" current={tool} onPick={pick} icon="fa-map-marker-alt" label="Survey spot" /> : null}
        {trail ? (
          <>
            <span className="fp-grow"></span>
            {trail}
          </>
        ) : null}
      </div>

      {tool === 'calibrate' && points.length === 2 ? (
        <form
          className="fp-calibrate"
          onSubmit={(e) => {
            e.preventDefault()
            applyCalibration()
          }}
        >
          <label htmlFor="fp-meters">Distance between the two points (m)</label>
          <input
            id="fp-meters"
            className="form-control input-sm"
            inputMode="decimal"
            autoFocus
            value={meters}
            onChange={(e) => setMeters(e.target.value)}
            placeholder="e.g. 12.5"
          />
          <button type="submit" className="btn btn-success fp-btn" disabled={!(Number(meters.replace(',', '.')) > 0)}>
            Apply
          </button>
          <button type="button" className="btn btn-default fp-btn" onClick={() => setPoints([])}>
            Again
          </button>
        </form>
      ) : null}

      {hint ? (
        <p className="fp-hint" role="status">
          {hint}
          {busy ? <i className="fas fa-spinner fa-spin fp-busy" aria-label="Working"></i> : null}
        </p>
      ) : null}

      <div className="fp-body">
        <div className="fp-map-wrap">
          {hasSurface ? (
            <Suspense fallback={<div className="fp-map fp-loading">Loading the plan...</div>}>
              <FloorPlanMap
                imageUrl={floor.image_url}
                widthPx={floor.image_w_px as number}
                heightPx={floor.image_h_px as number}
                gridScale={canvas && scale ? scale : undefined}
                snapPx={canvas && scale ? scale * SNAP_M : undefined}
                onZoom={setZoom}
                tool={tool}
                editable={editable}
                items={items}
                walls={walls}
                spots={spots}
                suggestions={mapSuggestions}
                coverage={layer === 'none' ? null : coverage.image}
                guide={guide}
                onPoint={onPoint}
                onSelect={setSelection}
                onWallDrawn={(seg) => {
                  if (!scale) return
                  const w: Wall = { x1: pxToCm(seg.x1, scale), y1: pxToCm(seg.y1, scale), x2: pxToCm(seg.x2, scale), y2: pxToCm(seg.y2, scale), m: material }
                  if (w.x1 === w.x2 && w.y1 === w.y2) return
                  change({ ...layout, walls: [...layout.walls, w] })
                }}
                onWallsModified={(list) => {
                  if (!scale) return
                  const next = layout.walls.slice()
                  for (const c of list) {
                    if (next[c.index]) next[c.index] = { ...next[c.index], x1: pxToCm(c.x1, scale), y1: pxToCm(c.y1, scale), x2: pxToCm(c.x2, scale), y2: pxToCm(c.y2, scale) }
                  }
                  change({ ...layout, walls: next })
                }}
                onItemMoved={(index, p) => {
                  if (!scale || !layout.items[index]) return
                  const at = cm(p)
                  change({ ...layout, items: layout.items.map((it, i) => (i === index ? { ...it, x_cm: at.x, y_cm: at.y } : it)) })
                }}
                onDropPayload={(payload, p) => {
                  if (editable) place(payload, p)
                }}
              />
            </Suspense>
          ) : (
            <div className="fp-map fp-empty">No plan uploaded.</div>
          )}
          {bar ? (
            <div className="fp-scalebar" data-scalebar={bar.meters} aria-label={`Scale: ${bar.meters} m`}>
              <span className="fp-scalebar-line" style={{ width: `${bar.px}px` }}></span>
              <span className="fp-scalebar-text">{bar.meters} m</span>
            </div>
          ) : null}
          <Legend layer={layer} thresholds={thresholds} busy={coverage.busy} />
        </div>

        <aside className="fp-side">
          <LayersPanel
            layer={layer}
            setLayer={setLayer}
            apChoice={apChoice}
            setApChoice={setApChoice}
            page={page}
            surveyPage={surveyPage}
            surveyIds={surveyIds}
            setSurveyIds={setSurveyIds}
            disabled={!scale}
          />

          {page.canWrite && scale ? (
            <section className="fp-panel" data-panel="survey">
              <h4>Wi-Fi survey</h4>
              {openSurvey === null ? (
                page.surveyDevices.length === 0 ? (
                  <p className="fp-muted">
                    No laptop can measure: turn on Connection Quality on a laptop of this company and connect it to Wi-Fi.
                  </p>
                ) : (
                  <div className="fp-survey-start">
                    <select className="form-control" aria-label="Laptop that measures" value={laptop} onChange={(e) => setLaptop(e.target.value)}>
                      <option value="">Choose the laptop...</option>
                      {page.surveyDevices.map((d) => (
                        <option key={d.id} value={String(d.id)}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                    <button type="button" className="btn btn-primary fp-btn-big" disabled={!laptop || busy} onClick={onStartSurvey}>
                      Start survey
                    </button>
                  </div>
                )
              ) : (
                <div className="fp-survey-run">
                  <p>
                    Survey running with <strong>{page.surveyDevices.find((d) => d.id === surveyPage?.surveys.find((s) => s.id === openSurvey)?.device_id)?.name ?? 'the laptop'}</strong>.
                    Walk with the laptop and tap where you are. {surveyPage?.spots.length ?? 0} spot(s).
                  </p>
                  <button type="button" className={`btn fp-btn-big ${tool === 'survey' ? 'btn-primary' : 'btn-default'}`} onClick={() => pick('survey')}>
                    Tap to add a spot
                  </button>
                  <button type="button" className="btn btn-success fp-btn-big" disabled={busy} onClick={onFinishSurvey}>
                    Finish survey
                  </button>
                </div>
              )}
            </section>
          ) : null}

          {editable && scale ? (
            <SuggestionsPanel
              data={sugg.data}
              loading={sugg.isLoading}
              plan={suggestions}
              selected={suggestionSel}
              busy={busy}
              onAccept={onAccept}
              onAcceptAll={onAcceptAll}
              onDismiss={onDismiss}
              onLocate={onLocate}
              onSelect={(id) => setSelection({ kind: 'suggestion', id })}
            />
          ) : null}

          <SelectionPanel
            page={page}
            layout={layout}
            selection={selection}
            surveyPage={surveyPage}
            scale={scale}
            editable={editable}
            canWrite={page.canWrite}
            onChangeItem={(index, next) => change({ ...layout, items: layout.items.map((it, i) => (i === index ? next : it)) })}
            onChangeWall={(index, next) => change({ ...layout, walls: layout.walls.map((w, i) => (i === index ? next : w)) })}
            onRemove={removeSelected}
            onDeleteSpot={onDeleteSpot}
          />

          {editable && scale ? <Palette page={page} layout={layout} armed={armed} onArm={arm} /> : null}
        </aside>
      </div>
    </div>
  )
}

function round2(v: number): number {
  return Math.round(v * 100) / 100
}

function ToolButton({ tool, current, onPick, icon, label, disabled = false }: { tool: MapTool; current: MapTool; onPick(t: MapTool): void; icon: string; label: string; disabled?: boolean }) {
  return (
    <button type="button" className={`btn fp-btn ${current === tool ? 'btn-primary' : 'btn-default'}`} aria-pressed={current === tool} disabled={disabled} onClick={() => onPick(tool)}>
      <i className={`fas ${icon}`} aria-hidden="true"></i> <span className="fp-btn-label">{label}</span>
    </button>
  )
}

function Legend({ layer, thresholds, busy }: { layer: Layer; thresholds: { good: number; fair: number; poor: number }; busy: boolean }) {
  if (layer === 'none') return null
  return (
    <div className="fp-legend" data-legend={layer}>
      <span className="fp-swatch is-good"></span> {'>='} {thresholds.good}
      <span className="fp-swatch is-fair"></span> {thresholds.fair}..{thresholds.good}
      <span className="fp-swatch is-poor"></span> {thresholds.poor}..{thresholds.fair}
      <span className="fp-swatch is-bad"></span> {'<'} {thresholds.poor} dBm
      {layer === 'survey' ? (
        <>
          <span className="fp-swatch is-masked"></span> not measured
          <em className="fp-legend-note">Interpolated between measured spots.</em>
        </>
      ) : (
        <em className="fp-legend-note">Predicted: an ESTIMATE from distance and walls (multi-wall model), not a measurement.</em>
      )}
      {busy ? <i className="fas fa-spinner fa-spin" aria-label="Calculating"></i> : null}
    </div>
  )
}

function LayersPanel(props: {
  layer: Layer
  setLayer(l: Layer): void
  apChoice: string
  setApChoice(k: string): void
  page: FloorPage
  surveyPage: SurveyPage | undefined
  surveyIds: number[]
  setSurveyIds(ids: number[]): void
  disabled: boolean
}) {
  const { layer, setLayer, apChoice, setApChoice, page, surveyPage } = props
  const surveys = surveyPage?.surveys ?? page.surveys
  const selected = surveyPage?.selected ?? []
  const all = surveys.length > 1 && selected.length === surveys.length
  return (
    <section className="fp-panel" data-panel="layers">
      <h4>Layers</h4>
      <div className="fp-radios" role="radiogroup" aria-label="Coverage layer">
        {(['none', 'survey', 'predicted'] as Layer[]).map((l) => (
          <label key={l} className="fp-radio">
            <input type="radio" name="fp-layer" value={l} checked={layer === l} disabled={props.disabled && l !== 'none'} onChange={() => setLayer(l)} />{' '}
            {l === 'none' ? 'Plan only' : l === 'survey' ? 'Survey coverage' : 'Predicted coverage'}
          </label>
        ))}
      </div>
      {layer === 'survey' ? (
        <>
          <label className="fp-field">
            Access point
            <select className="form-control input-sm" value={apChoice} onChange={(e) => setApChoice(e.target.value)}>
              <option value="best">Best access point at each spot</option>
              {page.aps.map((a) => (
                <option key={a.key} value={a.key}>
                  {a.name ?? a.key}
                </option>
              ))}
            </select>
          </label>
          {surveys.length > 0 ? (
            <label className="fp-field">
              Survey
              <select
                className="form-control input-sm"
                value={all ? 'all' : String(selected[0] ?? '')}
                onChange={(e) => props.setSurveyIds(e.target.value === 'all' ? surveys.map((s) => s.id) : [Number(e.target.value)])}
              >
                {surveys.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {s.started_at.slice(0, 16)} ({s.spots} spots){s.finished_at === null ? ' - running' : ''}
                  </option>
                ))}
                {surveys.length > 1 ? <option value="all">All surveys on this floor</option> : null}
              </select>
            </label>
          ) : (
            <p className="fp-muted">No survey yet on this floor.</p>
          )}
        </>
      ) : null}
      <p className="fp-muted fp-small">
        Access points are colored by what their clients saw in the last {page.apHours} hours: red = Weak AP (median signal below{' '}
        {page.apThresholds.weak_rssi} dBm or retries above {page.apThresholds.weak_retry_pct}%), orange = sticky clients, green = fine, gray = no
        clients. Devices show their current signal.
      </p>
    </section>
  )
}

function SelectionPanel(props: {
  page: FloorPage
  layout: Layout
  selection: MapSelection | null
  surveyPage: SurveyPage | undefined
  scale: number | null
  editable: boolean
  canWrite: boolean
  onChangeItem(index: number, next: FloorItem): void
  onChangeWall(index: number, next: Wall): void
  onRemove(): void
  onDeleteSpot(spotId: number, surveyId: number): void
}) {
  const { page, layout, selection, editable } = props
  if (!selection || selection.kind === 'suggestion') return null

  if (selection.kind === 'spot') {
    const spot = props.surveyPage?.spots.find((s) => s.id === selection.id)
    if (!spot) return null
    const map = props.surveyPage?.bssidAps ?? {}
    return (
      <section className="fp-panel fp-selection" data-selected="spot">
        <h4>Survey spot</h4>
        <p>
          Status: <strong>{spot.status}</strong>
          {spot.measured_at ? ` at ${spot.measured_at}` : ''}
          {spot.error ? ` - ${spot.error}` : ''}
          {spot.status === 'timeout' ? ' - the laptop did not answer. Check that it is online.' : ''}
        </p>
        {spot.readings.length > 0 ? (
          <table className="table table-condensed fp-readings">
            <thead>
              <tr>
                <th>Access point</th>
                <th>Band</th>
                <th className="num">Signal</th>
              </tr>
            </thead>
            <tbody>
              {spot.readings.slice(0, 12).map((r) => (
                <tr key={r.bssid} className={r.connected ? 'is-connected' : ''}>
                  <td>
                    {map[r.bssid]?.name ?? r.ssid ?? r.bssid}
                    <div className="fp-muted fp-small">{r.bssid}</div>
                  </td>
                  <td>{r.band ?? '-'}{r.channel ? ` / ch ${r.channel}` : ''}</td>
                  <td className="num">{Math.round(r.rssi_dbm)} dBm</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {props.canWrite ? (
          <button type="button" className="btn btn-danger fp-btn-big" onClick={() => props.onDeleteSpot(spot.id, spot.survey_id)}>
            Delete this spot
          </button>
        ) : null}
      </section>
    )
  }

  if (selection.kind === 'wall') {
    const wall = layout.walls[selection.index]
    if (!wall) return null
    const len = Math.hypot(wall.x2 - wall.x1, wall.y2 - wall.y1) / 100
    return (
      <section className="fp-panel fp-selection" data-selected="wall">
        <h4>Wall</h4>
        <p>
          {len.toFixed(2)} m, {wall.m} ({WALL_DB[wall.m]} dB)
        </p>
        {editable ? (
          <>
            <select className="form-control input-sm" aria-label="Material" value={wall.m} onChange={(e) => props.onChangeWall(selection.index, { ...wall, m: e.target.value as Material })}>
              {MATERIALS.map((m) => (
                <option key={m} value={m}>
                  {m} ({WALL_DB[m]} dB)
                </option>
              ))}
            </select>
            <button type="button" className="btn btn-danger fp-btn-big" onClick={props.onRemove}>
              Delete wall
            </button>
          </>
        ) : null}
      </section>
    )
  }

  const item = layout.items[selection.index]
  if (!item) return null
  const label = itemLabel(item, page.aps, page.devices)
  const ap = item.kind === 'ap' ? apOf(item.ap_group_key, page.aps) : undefined
  const device = item.kind === 'device' ? page.devices.find((d) => d.id === item.device_id) : undefined
  const signal = item.kind === 'device' && item.device_id !== null ? page.signals[String(item.device_id)] : undefined
  return (
    <section className="fp-panel fp-selection" data-selected={item.kind}>
      <h4>{label}</h4>
      {item.kind === 'ap' && item.ap_group_key !== null ? (
        ap ? (
          <dl className="fp-facts">
            <dt>Clients ({page.apHours}h)</dt>
            <dd>{ap.clients}</dd>
            <dt>Median signal</dt>
            <dd>{ap.median_rssi === null ? '-' : `${ap.median_rssi} dBm`}</dd>
            <dt>Median retries</dt>
            <dd>{ap.median_retry_pct === null ? '-' : `${ap.median_retry_pct}%`}</dd>
            <dt>Marks</dt>
            <dd>
              {ap.weak ? <span className="cq-aps-mark is-weak">Weak AP</span> : null}
              {ap.sticky.length ? (
                <span className="cq-aps-mark is-sticky" title={ap.sticky.map((c) => `${c.name || `Device ${c.id}`} (${c.rssi ?? '-'} dBm)`).join(', ')}>
                  Sticky clients: {ap.sticky.length}
                </span>
              ) : null}
              {!ap.weak && !ap.sticky.length ? '-' : null}
            </dd>
            <dt>BSSIDs</dt>
            <dd className="fp-small">{ap.bssids.join(' ')}</dd>
          </dl>
        ) : (
          <p className="fp-muted">This access point was not seen in the last {page.apHours} hours.</p>
        )
      ) : null}
      {item.kind === 'ap' && item.ap_group_key === null ? <p className="fp-muted">Planned access point: used only by the Predicted layer.</p> : null}
      {item.kind === 'device' ? (
        <dl className="fp-facts">
          <dt>Type</dt>
          <dd>{device?.type ?? '-'}</dd>
          <dt>Signal now</dt>
          <dd style={{ color: signal?.rssi_dbm != null ? undefined : COLORS.none }}>
            {signal?.rssi_dbm != null ? `${Math.round(signal.rssi_dbm)} dBm` : 'no Wi-Fi in the last 15 minutes'}
          </dd>
          {signal?.ssid ? (
            <>
              <dt>Network</dt>
              <dd>
                {signal.ssid}
                {signal.band ? ` (${signal.band} GHz)` : ''}
              </dd>
            </>
          ) : null}
        </dl>
      ) : null}
      {editable ? (
        <div className="fp-edit">
          {item.kind === 'note' || (item.kind === 'ap' && item.ap_group_key === null) ? (
            <label className="fp-field">
              Label
              <input
                className="form-control input-sm"
                maxLength={128}
                value={item.label ?? ''}
                onChange={(e) => props.onChangeItem(selection.index, { ...item, label: e.target.value })}
              />
            </label>
          ) : null}
          {item.kind === 'ap' ? (
            <div className="fp-ap-attrs">
              <label className="fp-field">
                Band
                <select
                  className="form-control input-sm"
                  value={item.attrs?.band ?? '5'}
                  onChange={(e) => props.onChangeItem(selection.index, { ...item, attrs: { ...(item.attrs ?? {}), band: e.target.value as '2.4' | '5' | '6' } })}
                >
                  <option value="2.4">2.4 GHz</option>
                  <option value="5">5 GHz</option>
                  <option value="6">6 GHz</option>
                </select>
              </label>
              <label className="fp-field">
                Tx power (dBm)
                <input
                  className="form-control input-sm"
                  type="number"
                  min={0}
                  max={36}
                  value={item.attrs?.tx_dbm ?? 20}
                  onChange={(e) => props.onChangeItem(selection.index, { ...item, attrs: { ...(item.attrs ?? {}), tx_dbm: Number(e.target.value) } })}
                />
              </label>
              <label className="fp-field">
                Height (m)
                <input
                  className="form-control input-sm"
                  type="number"
                  min={0}
                  max={30}
                  step={0.1}
                  value={item.attrs?.height_m ?? ''}
                  onChange={(e) =>
                    props.onChangeItem(selection.index, { ...item, attrs: { ...(item.attrs ?? {}), height_m: e.target.value === '' ? undefined : Number(e.target.value) } })
                  }
                />
              </label>
            </div>
          ) : null}
          <button type="button" className="btn btn-danger fp-btn-big" onClick={props.onRemove}>
            Remove from plan
          </button>
        </div>
      ) : null}
    </section>
  )
}

function SuggestionsPanel(props: {
  data: SuggestionsPage | undefined
  loading: boolean
  plan: PlanSuggestions
  selected: string | null
  busy: boolean
  onAccept(g: Suggestion): void
  onAcceptAll(): void
  onDismiss(g: Suggestion): void
  onLocate(ids: number[]): void
  onSelect(id: string): void
}) {
  const { data, plan } = props
  const [device, setDevice] = useState('')
  const noSurvey = data !== undefined && data.surveySpots === 0
  const candidates = data?.candidates ?? []
  const waiting = (data?.devices ?? []).filter((d) => d.status !== 'ok')
  return (
    <section className="fp-panel fp-suggest" data-panel="suggestions">
      <h4>Suggested positions</h4>
      <p className="fp-muted fp-small">
        {SUGGEST_EXPLAIN} It is an estimate, usually within 2-5 m.
      </p>
      {props.loading ? <p className="fp-muted">Loading...</p> : null}
      {plan.pending.length > 0 ? (
        <>
          <ul className="fp-suggest-list">
            {plan.pending.map((g) => (
              <li key={g.id} className={props.selected === g.id ? 'is-selected' : ''} data-suggestion={g.id}>
                <button type="button" className="fp-suggest-name" onClick={() => props.onSelect(g.id)}>
                  <strong>{g.label}</strong>
                  <span className="fp-entry-sub">
                    {g.detail}, within about {Math.max(2, Math.round(g.radius_cm / 100))} m
                  </span>
                </button>
                <div className="fp-suggest-actions">
                  <button type="button" className="btn btn-success btn-sm" onClick={() => props.onAccept(g)} aria-label={`Accept ${g.label}`}>
                    Accept
                  </button>
                  <button type="button" className="btn btn-default btn-sm" disabled={props.busy} onClick={() => props.onDismiss(g)} aria-label={`Dismiss ${g.label}`}>
                    Dismiss
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <button type="button" className="btn btn-primary fp-btn-big" onClick={props.onAcceptAll}>
            Accept all suggestions ({plan.pending.length})
          </button>
        </>
      ) : data && !noSurvey ? (
        <p className="fp-muted">No new suggestions.</p>
      ) : null}
      {plan.far.length > 0 ? (
        <ul className="fp-suggest-far">
          {plan.far.map((g) => (
            <li key={g.id} data-far={g.id}>
              <span>
                {g.label}: survey suggests {g.offsetM?.toFixed(1)} m away
              </span>
              <button type="button" className="btn btn-link btn-xs" onClick={() => props.onAccept(g)}>
                Move there
              </button>
              <button type="button" className="btn btn-link btn-xs" disabled={props.busy} onClick={() => props.onDismiss(g)}>
                Ignore
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {plan.apsWithoutData > 0 ? (
        <p className="fp-muted fp-small">
          {plan.apsWithoutData} access point(s): not enough data - heard at fewer than 3 spots or never above -70 dBm.
        </p>
      ) : null}

      <h4 className="fp-suggest-locate">Locate devices</h4>
      {noSurvey ? (
        <p className="fp-muted" role="note">
          {data?.message ?? 'Do a Wi-Fi survey on this floor first - positions are matched against it.'}
        </p>
      ) : candidates.length === 0 ? (
        <p className="fp-muted">No device can be located: a device needs Connection Quality on and Wi-Fi in the last 24 hours.</p>
      ) : (
        <>
          <p className="fp-muted fp-small">Each device measures the Wi-Fi around it once; its position is matched against the survey. Macs cannot do it (macOS hides the access points), and Windows needs Location on.</p>
          <button type="button" className="btn btn-default fp-btn-big" disabled={props.busy || noSurvey} onClick={() => props.onLocate([])}>
            Locate devices ({candidates.length})
          </button>
          <div className="fp-suggest-one">
            <select className="form-control input-sm" aria-label="Device to locate" value={device} onChange={(e) => setDevice(e.target.value)}>
              <option value="">One device...</option>
              {candidates.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name}
                </option>
              ))}
            </select>
            <button type="button" className="btn btn-default btn-sm" disabled={!device || props.busy} onClick={() => props.onLocate([Number(device)])}>
              Locate
            </button>
          </div>
        </>
      )}
      {waiting.length > 0 ? (
        <ul className="fp-suggest-waiting">
          {waiting.map((d) => (
            <li key={d.device_id} data-locate={d.device_id} className={`is-${d.status}`}>
              <strong>{d.name}</strong>: {locateStatus(d)}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}

function Palette({ page, layout, armed, onArm }: { page: FloorPage; layout: Layout; armed: string | null; onArm(payload: string): void }) {
  const [filter, setFilter] = useState('')
  const placedAps = new Set(layout.items.filter((i) => i.kind === 'ap' && i.ap_group_key).map((i) => i.ap_group_key as string))
  const placedDevices = new Set(layout.items.filter((i) => i.kind === 'device').map((i) => i.device_id as number))
  const f = filter.trim().toLowerCase()
  const devices = page.devices.filter((d) => !f || d.name.toLowerCase().includes(f) || (d.type ?? '').toLowerCase().includes(f)).slice(0, 200)
  const entry = (payload: string, label: string, sub: string, placed: boolean) => (
    <li key={payload}>
      <button
        type="button"
        draggable
        className={`fp-entry${armed === payload ? ' is-armed' : ''}${placed ? ' is-placed' : ''}`}
        onDragStart={(e) => e.dataTransfer.setData('text/plain', payload)}
        onClick={() => onArm(payload)}
        title={placed ? 'Already on this plan - place it again to move it' : 'Drag onto the plan, or tap and then tap the plan'}
      >
        <span className="fp-entry-name">{label}</span>
        <span className="fp-entry-sub">{sub}</span>
      </button>
    </li>
  )
  return (
    <section className="fp-panel fp-palette" data-panel="palette">
      <h4>Access points</h4>
      <ul className="fp-list">
        {page.aps.map((a) =>
          entry(
            `ap:${a.key}`,
            a.name ?? a.key,
            `${a.clients} client(s)${a.median_rssi !== null ? `, ${a.median_rssi} dBm` : ''}${placedAps.has(a.key) || a.keys.some((k) => placedAps.has(k)) ? ' - on plan' : ''}`,
            placedAps.has(a.key) || a.keys.some((k) => placedAps.has(k)),
          ),
        )}
        {entry('planned', 'Planned access point', 'not installed yet - for the Predicted layer', false)}
        {entry('note', 'Note', 'a label on the plan', false)}
      </ul>
      <h4>Devices</h4>
      <input className="form-control input-sm" placeholder="Filter devices" aria-label="Filter devices" value={filter} onChange={(e) => setFilter(e.target.value)} />
      <ul className="fp-list">
        {devices.map((d) => entry(`device:${d.id}`, d.name, `${d.type ?? 'Device'}${placedDevices.has(d.id) ? ' - on plan' : ''}`, placedDevices.has(d.id)))}
      </ul>
    </section>
  )
}
